import React from 'react';
import { useNavigate, Link } from 'react-router-dom';

// Public /how-to guide — same light container/styling as FAQPage so it reads
// consistently and prints cleanly to PDF (the "← Back" control is print:hidden).
// All body copy is brand-voice-approved and rendered VERBATIM from backtick
// strings (so the UI-label double-quotes and apostrophes need no escaping).

const WALKTHROUGH = [
  {
    heading: `What EsuSol is`,
    paras: [
      `EsuSol is a record-keeping tool for savings circles, also known as susu, ajo, or esusu. It keeps track of who has contributed, whose turn it is to collect, and the full history of your group, all in one clear place.`,
      `One thing to be clear about: EsuSol does not hold, move, or process any money. Your group sends payments to each other directly, using whatever method you already trust, such as Zelle, Cash App, Venmo, or cash. EsuSol simply keeps an accurate, timestamped record of it all.`,
    ],
  },
  {
    heading: `Setting up a group (Admin)`,
    paras: [
      `Start by creating your group. You will give it a name, set the contribution amount for each hand, and choose how often the group collects, such as weekly or every two weeks. This becomes the rhythm your group follows for the whole cycle.`,
    ],
  },
  {
    heading: `Adding members (Admin)`,
    paras: [
      `You add each member yourself. Click Add Member and give them a display name, and EsuSol creates a personal invite link just for that person. Share their link with them by WhatsApp, or copy it and send it however you like.`,
      `When a member opens their link, they finish their own onboarding. They confirm their legal name, verify their phone number, and accept the group agreement. So you decide who is in the group, and each member confirms their own details and gives their own consent.`,
    ],
  },
  {
    heading: `Rotation and starting the cycle (Admin)`,
    paras: [
      `Every member holds a position in the rotation, which decides the order in which people collect. You can set the order yourself or let EsuSol assign it for you. Once everyone has joined and the order is set, you start the cycle, and the first round begins.`,
    ],
  },
  {
    heading: `How a round works`,
    paras: [
      `In each round, the members who are not collecting pay their contribution to the admin. Each member sends their payment to the admin using the group's method, then taps "I Paid" on their dashboard. The admin confirms each payment as it comes in, which keeps the record accurate.`,
    ],
  },
  {
    heading: `When it is your turn to collect`,
    paras: [
      `This is the part that feels different, so here is exactly how it works.`,
      `In the round when you collect, you do not pay a contribution. Instead, the admin sends you the total that the other members contributed that round.`,
      `For example, in a group of four people each paying 100, when your turn comes the admin sends you 300, the total from the other three, and you skip your own payment for that round. Then you go back to contributing in every other round, so that everyone gets their full turn before the cycle ends.`,
      `Your dashboard will show "Collecting" during your round, instead of a payment due, so you always know when it is your turn.`,
    ],
  },
  {
    heading: `Sending and confirming a payout`,
    paras: [
      `Once the other members have paid, the admin sends the payout to the collector. The admin clicks Send Payout on the collector's row and enters the details, then sends the funds to the collector directly. The admin does not wait for the collector's own contribution, because the collector does not contribute in the round they collect.`,
      `After the payout is sent, the collector confirms they received the funds by tapping Confirm Receipt on their dashboard. This creates a clear, two-sided record, one side showing the payout was sent and the other showing it was received. Once receipt is confirmed, the round moves forward.`,
    ],
  },
  {
    heading: `Late payments and covering a member`,
    paras: [
      `If a member has not paid by their due date, their status stays pending until the admin confirms their payment arrives. If someone falls behind, the admin can add a late fee, which is tracked separately from their contribution and gives the admin a record to decide how to handle that member going forward.`,
      `The admin can also cover a member, so the round stays on schedule. When that happens, the record shows that the member owes the admin that amount.`,
    ],
  },
  {
    heading: `How a cycle ends`,
    paras: [
      `A cycle is complete when every hand has collected once. Because each member contributes in every round except their own collection round, the pot rotates fully through the group, and everyone receives their full turn.`,
    ],
  },
  {
    heading: `Your money and your privacy`,
    paras: [
      `EsuSol never holds or moves your money. Every payment happens directly between members and the admin.`,
      `Within your group, your name, rotation position, and payment status are visible to your admin and the other members, because a savings circle depends on everyone being able to see who has paid and whose turn it is. Your information is never sold or shared for marketing. You can read the full details in our Privacy Policy.`,
    ],
  },
];

const HowToPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <button
          onClick={() => navigate(-1)}
          className="text-neutral-600 hover:text-neutral-900 mb-4 inline-block print:hidden"
        >
          ← Back
        </button>

        <h1 className="text-3xl font-bold text-neutral-900 mb-4">How EsuSol Works</h1>

        <p className="text-neutral-700 mb-4">
          {`EsuSol keeps your savings group organized and clear. It tracks every contribution and payout with a timestamped record, so everyone can see who has paid and whose turn it is to collect. EsuSol never holds or moves your money. Your group handles payments directly, the way you always have, and EsuSol keeps the record straight.`}
        </p>
        <p className="text-neutral-700 mb-8">
          {`Whether you are running a group or joining one, here is everything you need to know.`}
        </p>

        {/* Quick Start */}
        <h2 className="text-2xl font-bold text-neutral-900 mb-4">Quick Start</h2>

        <div className="bg-neutral-100 rounded-lg border border-neutral-200 p-6 mb-4">
          <p className="font-semibold text-neutral-900 mb-3">Running a group (Admin)</p>
          <ol className="list-decimal pl-5 space-y-2 text-neutral-700">
            <li>{`Create your group, and set the contribution amount and how often you collect.`}</li>
            <li>{`Add each member and share their personal invite link.`}</li>
            <li>{`Set the rotation order, or let EsuSol assign it.`}</li>
            <li>{`Start the cycle once everyone has joined.`}</li>
            <li>{`Each round, confirm the members' payments, then send the payout to whoever is collecting.`}</li>
          </ol>
        </div>

        <div className="bg-neutral-100 rounded-lg border border-neutral-200 p-6 mb-8">
          <p className="font-semibold text-neutral-900 mb-3">Joining a group (Member)</p>
          <ul className="list-disc pl-5 space-y-2 text-neutral-700">
            <li>{`Your group admin sends you a personal invite link.`}</li>
            <li>{`Open it, confirm your name, verify your phone, and accept the group agreement.`}</li>
            <li>{`Each round, pay your contribution and tap "I Paid."`}</li>
            <li>{`When it is your turn to collect, you skip your contribution and receive the pot.`}</li>
          </ul>
        </div>

        {/* The Full Walkthrough */}
        <h2 className="text-2xl font-bold text-neutral-900 mb-4">The Full Walkthrough</h2>
        <div className="space-y-6">
          {WALKTHROUGH.map((section, i) => (
            <section key={i}>
              <h3 className="text-lg font-bold text-neutral-900 mb-2">{section.heading}</h3>
              {section.paras.map((para, j) => (
                <p key={j} className={`text-neutral-700 ${j < section.paras.length - 1 ? 'mb-3' : ''}`}>
                  {para}
                </p>
              ))}
            </section>
          ))}
        </div>

        {/* Call to action — the action step after the walkthrough */}
        <div className="mt-10 bg-neutral-100 rounded-lg border border-neutral-200 p-6 text-center">
          <h2 className="text-xl font-bold text-neutral-900 mb-1">Ready to start your group?</h2>
          <p className="text-neutral-600 mb-4">Create your group and try EsuSol today.</p>
          <a
            href="https://esusol.controlpilotsystems.com/"
            className="inline-block px-6 py-3 bg-primary-600 text-neutral-50 rounded-lg font-medium hover:bg-primary-700"
          >
            Get Started
          </a>
        </div>

        {/* Closing — FAQ is a real link to the /faq route */}
        <p className="text-neutral-700 mt-8 pt-6 border-t border-neutral-200">
          {`Have a quick question? Visit our `}
          <Link to="/faq" className="text-primary-600 hover:text-primary-700 underline">FAQ</Link>
          {` for fast answers for both admins and members.`}
        </p>
      </div>
    </div>
  );
};

export default HowToPage;
